import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormControl, Input, VStack, FormErrorMessage } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useSelector } from 'react-redux';
import { selectNLCCompactStatusList, selectPTCompactStatusList, selectOTCompactStatusList } from '../enum/enumSlice';
import { useUpdateStateMutation } from '@/app/services/nucleus';
import { toPactPercent, toNucleusPercent, isRealString } from '@/utils/helpers';
import LoaderOverlay from '../common/LoaderOverlay';
import { useRole } from '@/hooks/useRole';
import ModalComponent from '../common/ModalComponent';
import { TbLayoutCards } from 'react-icons/tb';
import FormRow from '@/features/user/FormRow.jsx';
import { useNotification } from '@/hooks/useNotification';

const defaultFormData = {
  minRegularPay: '',
  workersCompRate: '',
  exciseTax: '',
  nlcCompactStatus: null,
  ptCompactStatus: null,
  otCompactStatus: null,
  errors: {},
};

const StateModal = ({ isOpen, onClose, state }) => {
  useRole('admin');

  const [updateState, { isLoading, isError, isSuccess, error: updateStateErrorMsg, reset: resetUpdateState }] = useUpdateStateMutation();

  useNotification(isSuccess, resetUpdateState, isSuccess, 'success', 'State updated successfully');
  useNotification(isError, resetUpdateState, isError, 'error', updateStateErrorMsg?.data?.detail || 'Error updating state');

  const nlcCompactStatusList = useSelector(selectNLCCompactStatusList);
  const ptCompactStatusList = useSelector(selectPTCompactStatusList);
  const otCompactStatusList = useSelector(selectOTCompactStatusList);

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    if (state) {
      setFormData({
        minRegularPay: state?.minimum_regular_pay_rate,
        workersCompRate: toPactPercent(state?.workers_compensation_rate),
        exciseTax: toPactPercent(state?.excise_tax),
        nlcCompactStatus: nlcCompactStatusList.find((option) => option.value === state?.nlc_compact_status) || null,
        ptCompactStatus: ptCompactStatusList.find((option) => option.value === state?.pt_compact_status) || null,
        otCompactStatus: otCompactStatusList.find((option) => option.value === state?.ot_compact_status) || null,
        errors: {},
      });
    }
  }, [state, nlcCompactStatusList, ptCompactStatusList, otCompactStatusList]);

  const validatePercent = (value) => {
    if (value === '') return;
    const numberValue = Number(value);
    if (Number.isNaN(numberValue) || numberValue < 0 || numberValue > 100) {
      return 'Value must be a number between 0 and 100';
    }
  };

  const validateDollars = (value) => {
    if (value === '') return;
    const numberValue = Number(value ?? 0);
    if (Number.isNaN(numberValue) || numberValue < 0) {
      return 'Value must be a non-negative number';
    }
  };

  const handleInputChange = useCallback((key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
      errors: {
        ...prev.errors,
        ...(key === 'minRegularPay' && { minRegularPay: validateDollars(value) }),
        ...(key === 'workersCompRate' && { workersCompRate: validatePercent(value) }),
        ...(key === 'exciseTax' && { exciseTax: validatePercent(value) }),
      },
    }));
  }, []);

  const isFormValid = () => {
    return !Object.values(formData?.errors).some(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetUpdateState();

    if (!isFormValid()) {
      return;
    }

    const statePayload = {
      name: state.name, //read-only field, required by API to be in payload
      minimum_regular_pay_rate: formData?.minRegularPay ? Number(formData.minRegularPay) : 0,
      workers_compensation_rate: formData?.workersCompRate ? toNucleusPercent(formData.workersCompRate) : 0,
      excise_tax: formData?.exciseTax ? toNucleusPercent(formData.exciseTax) : 0,
      nlc_compact_status: formData.nlcCompactStatus?.value || null,
      pt_compact_status: formData.ptCompactStatus?.value || null,
      ot_compact_status: formData.otCompactStatus?.value || null,
    };

    try {
      await updateState({ stateId: state.id, state: statePayload }).unwrap();
      handleClose();
    } catch (error) {
      console.log('Error Updating State', error);
    }
  };

  const handleClose = () => {
    resetUpdateState();
    setFormData(defaultFormData);
    onClose();
  };

  return (
    <ModalComponent
      title={'Edit State'}
      icon={<TbLayoutCards />}
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      handleConfirm={handleSubmit}
      primaryText="Save"
      secondaryText="Cancel"
      isError={!isFormValid()}
    >
      <VStack spacing={4} position="relative">
        <LoaderOverlay loading={isLoading} />

        <FormControl aria-label="name-control">
          <FormRow label="State Name" templateColumns={{ base: '1fr', md: '200px auto' }} width={'100%'}>
            <Input id="name" focusBorderColor="brand.700" value={state?.displayName} isReadOnly background="gray.50" />
          </FormRow>
        </FormControl>

        <FormControl aria-label="minimum-regular-pay-control" isInvalid={formData.errors?.minRegularPay}>
          <FormRow id="minimum_regular_pay" label="Minimum Regular Pay ($)" templateColumns={{ base: '1fr', md: '200px auto' }} width={'100%'}>
            <VStack>
              <Input
                id="minimum_regular_pay"
                focusBorderColor="brand.700"
                type="number"
                step="0.01"
                value={formData.minRegularPay}
                onChange={(e) => handleInputChange('minRegularPay', e.target.value)}
                onBlur={(e) => {
                  if (e.target.value) {
                    const value = Number.parseFloat(e.target.value).toFixed(2);
                    handleInputChange('minRegularPay', value);
                  }
                }}
              />
              {isRealString(formData.errors?.minRegularPay) ? <FormErrorMessage>{formData.errors?.minRegularPay}</FormErrorMessage> : null}
            </VStack>
          </FormRow>
        </FormControl>

        <FormControl aria-label="workers-comp-rate-control" isInvalid={formData.errors?.workersCompRate}>
          <FormRow id="workers_comp_rate" label="Workers Comp Rate (%)" templateColumns={{ base: '1fr', md: '200px auto' }} width={'100%'}>
            <VStack>
              <Input
                id="workers_comp_rate"
                focusBorderColor="brand.700"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.workersCompRate}
                onChange={(e) => handleInputChange('workersCompRate', e.target.value)}
              />
              {isRealString(formData.errors?.workersCompRate) ? <FormErrorMessage>{formData.errors?.workersCompRate}</FormErrorMessage> : null}
            </VStack>
          </FormRow>
        </FormControl>

        <FormControl aria-label="excise-tax-control" isInvalid={formData.errors?.exciseTax}>
          <FormRow id="excise_tax" label="Excise Tax Rate (%)" templateColumns={{ base: '1fr', md: '200px auto' }} width={'100%'}>
            <VStack>
              <Input
                id="excise_tax"
                focusBorderColor="brand.700"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.exciseTax}
                onChange={(e) => handleInputChange('exciseTax', e.target.value)}
              />
              {isRealString(formData.errors?.exciseTax) ? <FormErrorMessage>{formData.errors?.exciseTax}</FormErrorMessage> : null}
            </VStack>
          </FormRow>
        </FormControl>

        <FormControl aria-label="nlc-compact-status-control" isInvalid={formData.errors?.nlcCompactStatus}>
          <FormRow id="nlc_compact_status" label="NLC Compact Status" templateColumns={{ base: '1fr', md: '200px auto' }} width={'100%'}>
            <Select
              id="nlc_compact_status"
              aria-label="nlc_compact_status"
              placeholder="Select NLC Compact Status"
              focusBorderColor="brand.700"
              options={nlcCompactStatusList}
              value={formData.nlcCompactStatus}
              onChange={(value) => handleInputChange('nlcCompactStatus', value)}
              isClearable
            />
          </FormRow>
        </FormControl>

        <FormControl aria-label="pt-compact-status-control" isInvalid={formData.errors?.ptCompactStatus}>
          <FormRow id="pt_compact_status" label="PT Compact Status" templateColumns={{ base: '1fr', md: '200px auto' }} width={'100%'}>
            <Select
              id="pt_compact_status"
              aria-label="pt_compact_status"
              placeholder="Select PT Compact Status"
              focusBorderColor="brand.700"
              options={ptCompactStatusList}
              value={formData.ptCompactStatus}
              onChange={(value) => handleInputChange('ptCompactStatus', value)}
              isClearable
            />
          </FormRow>
        </FormControl>

        <FormControl aria-label="ot-compact-status-control" isInvalid={formData.errors?.otCompactStatus}>
          <FormRow id="ot_compact_status" label="OT Compact Status" templateColumns={{ base: '1fr', md: '200px auto' }} width={'100%'}>
            <Select
              id="ot_compact_status"
              aria-label="ot_compact_status"
              placeholder="Select OT Compact Status"
              focusBorderColor="brand.700"
              options={otCompactStatusList}
              value={formData.otCompactStatus}
              onChange={(value) => handleInputChange('otCompactStatus', value)}
              isClearable
            />
          </FormRow>
        </FormControl>
      </VStack>
    </ModalComponent>
  );
};

StateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  state: PropTypes.object,
};

export default StateModal;
